/**
 * The Election header's dates. One date reads as a single line; two or more
 * (or one with a possible runoff after it) read as a rail. Each date says
 * where it stands: days away, polls closing, results pending, or decided,
 * with the Race counts that belong to it. A runoff nobody has advanced into
 * is drawn as possible, and leaves once its source ballot is decided.
 */
import type { PublicCycle } from "../api";
import { h } from "../dom";
import { shortBallotDate } from "../ballot-model";
import {
  ballotProgress,
  chicagoClock,
  type BallotProgress,
} from "../../../lib/election-ballot-progress";

export type TimelineState = BallotProgress["phase"] | "possible";

export interface TimelineStep {
  slug: string;
  label: string;
  date: string;
  /** "Today", "Tue, Nov 3" ahead, "Mar 17" once held. */
  when: string;
  state: TimelineState;
  status: string;
  details: string[];
  current: boolean;
}

const UPCOMING_DATE = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

const races = (n: number) => `${n} ${n === 1 ? "race" : "races"}`;

/** "19:00" → "7 p.m.", "19:30" → "7:30 p.m." */
export function pollsCloseLabel(time: string): string {
  const [hour, minute] = time.split(":").map(Number);
  const h12 = hour % 12 || 12;
  return `${h12}${minute ? `:${String(minute).padStart(2, "0")}` : ""} ${hour < 12 ? "a.m." : "p.m."}`;
}

function countdown(date: string, today: string): string {
  const days = Math.round((Date.parse(`${date}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
  return days === 1 ? "Tomorrow" : `${days} days away`;
}

export function timelineSteps(cycle: PublicCycle, now = new Date()): TimelineStep[] {
  const today = chicagoClock(now).date;
  const ballots = [...cycle.ballots].sort((a, b) => a.date.localeCompare(b.date));
  const progress = ballotProgress(ballots, cycle.races, now);
  return ballots.flatMap((b, i): TimelineStep[] => {
    const p = progress[i];
    if (p.notNeeded) return [];
    const before = progress[i - 1];
    const counted = b.stage !== "primary";
    const step: TimelineStep = {
      slug: b.slug,
      label: b.label,
      date: b.date,
      when: p.phase === "today" ? "Today" : p.phase === "upcoming" ? UPCOMING_DATE.format(new Date(`${b.date}T12:00:00Z`)) : shortBallotDate(b.date),
      state: p.possible ? "possible" : p.phase,
      status: "",
      details: [],
      current: b.slug === cycle.liveBallot,
    };
    if (p.possible) {
      step.status = "Possible";
      step.details.push("Only if a race isn't won outright");
    } else if (p.phase === "upcoming") {
      step.status = countdown(b.date, today);
    } else if (p.phase === "today") {
      step.status = `Polls close at ${pollsCloseLabel(b.pollsClose)}`;
    } else if (p.phase === "pending") {
      step.status = "Results pending";
      if (counted && p.called > 0) step.details.push(`${p.called} of ${races(p.races)} called`);
    } else {
      step.status = "Decided";
      if (counted && p.wonOutright > 0) step.details.push(`${p.wonOutright} won outright`);
      if (progress[i + 1]?.notNeeded) step.details.push("No runoff needed");
    }
    // A runoff's Races are the ones that advanced into it, counted once, here.
    if (b.stage === "runoff" && !p.possible && before?.advanced) step.details.push(races(before.advanced));
    return [step];
  });
}

export function ballotTimeline(cycle: PublicCycle, now = new Date()): HTMLElement | null {
  const steps = timelineSteps(cycle, now);
  if (steps.length === 0) return null;
  return h(
    "ol",
    {
      class: `bsc-ballot-timeline${steps.length === 1 ? " bsc-ballot-timeline--single" : ""}`,
      "aria-label": steps.length === 1 ? "Election date" : "Election dates",
    },
    steps.map((s) =>
      h(
        "li",
        {
          class: "bsc-ballot-timeline__step",
          "data-ballot-state": s.state,
          "aria-current": s.current && steps.length > 1 ? "step" : null,
        },
        h("span", { class: "bsc-ballot-timeline__marker", "aria-hidden": "true" }),
        h("time", { class: "bsc-ballot-timeline__date", datetime: s.date }, s.when),
        h("span", { class: "bsc-ballot-timeline__label" }, s.label),
        h(
          "span",
          { class: "bsc-ballot-timeline__status" },
          h("span", null, s.status),
          s.details.map((d) => h("span", { class: "bsc-ballot-timeline__detail" }, d)),
        ),
      ),
    ),
  );
}
